import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  Ref,
  useImperativeHandle,
} from "react";
import MapContext from "../contexts/mapContext";
import { transformExtent, transform, fromLonLat } from "ol/proj";
import * as ol from "ol";
import LogService from "../../../services/logService";
import LogDirectory from "../../../services/logDirectory";

const logService = new LogService(LogDirectory.EnableMapLogging);

interface Props {
  children: any;
  zoom: any;
  center: any;
  onClick?: any | undefined;
  onPointerMove?: any | undefined;
}

export interface MapControlRef {
  refresh: () => void;
  checkPositionInView: (positionInView: number[]) => void;
  getMap: () => ol.Map | undefined;
}

const MapControl = forwardRef((props: Props, ref: Ref<MapControlRef>) => {
  const { children, zoom, center, onClick, onPointerMove } = props;

  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<ol.Map>();

  useImperativeHandle(ref, () => ({ refresh, checkPositionInView, getMap }));

  const getMap = () => {
    return map;
  };

  const refresh = () => {
    logService.log("mapControl:refresh()");
    if (map) {
      map.updateSize();
    }
  };

  const checkPositionInView = (positionInView: number[]) => {
    //console.log("mapControl:checkPositionInView()");

    if (!map) {
      return;
    }

    if (!positionInView) {
      return;
    }

    if (!map.getView().getCenter()) {
      return;
    }

    let lon = positionInView[0];
    let lat = positionInView[1];

    let extent = map.getView().calculateExtent(map.getSize());
    extent = transformExtent(extent, "EPSG:3857", "EPSG:4326");

    let isLonInExtent = extent[0] < lon && lon < extent[2];
    let isLatInExtent = extent[1] < lat && lat < extent[3];
    let isLonLatInExtent = isLonInExtent && isLatInExtent;

    if (!isLonLatInExtent) {
      logService.log("mapControl:setPositionInView()");
      map.getView().setCenter(transform([lon, lat], "EPSG:4326", "EPSG:3857"));
    }
  };

  // on component mount
  useEffect(() => {
    //console.log("mapControl:init()");

    let options = {
      view: new ol.View(),
      layers: [],
      controls: [],
      overlays: [],
    };

    let mapObject = new ol.Map(options);

    if (mapRef.current !== null) {
      mapObject.setTarget(mapRef.current);
      setMap(mapObject);
    }

    return () => mapObject.setTarget(undefined);
  }, []);

  useEffect(() => {
    //console.log("mapControl:onClick()");

    if (!map) {
      return;
    }

    const onMoveStart = (evt: any) => {
      map.getViewport().style.cursor = "grabbing";
    };

    const onMoveEnd = (evt: any) => {
      map.getViewport().style.cursor = "default";
    };

    map.on("singleclick", onClick);
    map.on("movestart", onMoveStart);
    map.on("moveend", onMoveEnd);
    map.on("pointermove", onPointerMove);

    return () => {
      map.un("singleclick", onClick);
      map.un("movestart", onMoveStart);
      map.un("moveend", onMoveEnd);
      map.un("pointermove", onPointerMove);
    };
  }, [map, onClick, onPointerMove]);

  // zoom change handler
  useEffect(() => {
    //console.log("mapControl:setZoom()");

    if (!map) {
      return;
    }

    if (!zoom) {
      return;
    }

    map.getView().setZoom(zoom);
  }, [map, zoom]);

  // center change handler
  useEffect(() => {
    //console.log("mapControl:setCenter()", center);

    if (!map) {
      return;
    }

    if (center.length === 0) {
      return;
    }

    let centerTransform = fromLonLat(center);
    map.getView().setCenter(centerTransform);
  }, [map, center]);

  return (
    <MapContext.Provider value={map}>
      <div ref={mapRef} className="ol-map">
        {children}
      </div>
    </MapContext.Provider>
  );
});
export default MapControl;
